import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerSectionPath } from '../lib/navigation.js';
import { api } from '../lib/api.js';
import QlikUpsellCard from '../components/QlikUpsellCard.jsx';
import QlikOfferPanel from '../components/QlikOfferPanel.jsx';
import SectionCard from '../components/SectionCard.jsx';
import PageState from '../components/PageState.jsx';
import Notice from '../components/Notice.jsx';
import StatusWord from '../components/StatusWord.jsx';
import { QLIK_S3_CONNECTION_NAME, QLIK_SPACE_PLACEHOLDER, QLIK_FIRST_LOAD_SAMPLE } from '../../../shared/qlik-connection.mjs';

/**
 * The customer's Qlik Cloud section (ENTRAID-282), moved out of
 * Configuration: status, enable or disable (operators only), and the exact steps for the
 * customer's Qlik Cloud admin to create the Amazon S3 V2 data connection,
 * field by field with this customer's values and the one connection name
 * the apps use (portal/shared/qlik-connection.mjs), including the explicit
 * Reveal credentials action.
 *
 * Shown to operators, the customer's admins, and holders of the customer's
 * qlik-credentials role (that last one sees nothing else of the customer).
 * Its data comes from GET /customers/{id}/qlik-integration, an allowlisted
 * view without the secret; the secret only ever comes from the audited,
 * rate limited, uncached POST /customers/{id}/qlik-secret.
 *
 * Qlik app distribution will be a card of its own in this section later;
 * it is not built yet.
 *
 * ENTRAID-347 redesign (plan C7): the page title is the section's PageHeader,
 * drawn by the caller (pages/CustomerSections.jsx), so this page renders no h1.
 * Each job is its own SectionCard: the offer, the integration status and
 * controls, and the connection setup steps.
 */

export default function CustomerQlik({ customerId, isOperator, marketplaceListingUrl = '' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.getQlikIntegration(customerId).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message));
  }, [customerId]);

  useEffect(() => { setData(null); load(); }, [load]);

  if (error) return <PageState state="error" title="Qlik Cloud could not be loaded" message={error} onRetry={load} />;
  if (!data) return <PageState state="loading" header={false} cards={2} label="Loading Qlik Cloud" />;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Enable and disable: operators only (the POST refuses everyone else too),
          never while an operator views the portal as the client. */}
      <QlikOfferPanel data={data} />
      <QlikIntegrationCard data={data} marketplaceListingUrl={marketplaceListingUrl} canManage={isOperator === true && data.canManage === true} onRefresh={load} />
      {data.qlik.enabled === true && <QlikSetupSteps data={data} />}
    </div>
  );
}

function QlikIntegrationCard({ data, canManage, onRefresh, marketplaceListingUrl }) {
  const { customerId, qlik } = data;
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [polling, setPolling] = useState(false);

  const isPending = qlik.enabled === 'pending' || qlik.enabled === 'disabling';
  const isEnabled = qlik.enabled === true;
  const isDeployed = data.status === 'deployed' || data.status === 'active';
  // Pricing decision (2026-09-23): Qlik Cloud apps are included from the
  // Professional tier upward. Disabling stays allowed at any tier; only
  // enabling is gated (enforced server-side too, in customer-qlik.mjs).
  const tierExcludesQlik = data.tierName === 'starter' && !isEnabled;

  // Poll for completion while a stack update is in progress.
  useEffect(() => {
    if (!isPending && !polling) return undefined;
    const interval = setInterval(() => { onRefresh(); }, 8000);
    return () => clearInterval(interval);
  }, [isPending, polling, onRefresh]);

  // Stop polling once the pending state resolves.
  useEffect(() => {
    if (polling && !isPending) {
      setPolling(false);
      setBusy(false);
      if (qlik.enabled === true) {
        setResult({ kind: 'ok', text: 'Qlik integration enabled. The connection details are below; reveal the credentials when you create the Qlik Cloud connection.' });
      } else if (qlik.error) {
        setResult({ kind: 'err', text: qlik.error });
      }
    }
  }, [polling, isPending, qlik.enabled, qlik.error]);

  async function toggle(action) {
    setBusy(true);
    setResult(null);
    setConfirming(false);
    try {
      const res = await api.toggleQlik(customerId, action);
      if (res.stackUpdate === 'in-progress') {
        setPolling(true);
        setResult({ kind: 'info', text: 'Stack update in progress, this takes about 60-90 seconds...' });
        onRefresh(); // Pick up the pending state right away.
      } else {
        setResult({
          kind: 'ok',
          text: action === 'enable'
            ? 'Qlik integration enabled. IAM credentials created and stored in SSM Parameter Store.'
            : 'Qlik integration disabled. IAM resources removed.',
        });
        setBusy(false);
        onRefresh();
      }
    } catch (e) {
      setResult({ kind: 'err', text: e.message });
      setBusy(false);
    }
  }

  const showBusy = busy || isPending;
  const statusLabel = isPending ? 'Updating...' : isEnabled ? 'Enabled' : 'Disabled';
  const statusTone = isPending ? 'warn' : isEnabled ? 'good' : 'off';
  const resultTone = result?.kind === 'ok' ? 'good' : result?.kind === 'info' ? 'info' : 'bad';

  return (
    <SectionCard
      title="Qlik Cloud integration"
      description="Read-only credentials for the Qlik Cloud Amazon S3 data connection to this customer's onGuard data."
      action={<StatusWord tone={statusTone}>{statusLabel}</StatusWord>}
      bodyClassName="space-y-4"
    >
      {isPending && (
        <Notice tone="warn" role="status" titleAs="p" title="CloudFormation stack update in progress">
          This usually takes 60 to 90 seconds.
        </Notice>
      )}

      {/* The quote panel above already says it (ENTRAID-348), so the older upsell card shows only when that panel does not. */}
      {tierExcludesQlik && (data.hasQlikCloud !== false || canManage) && (
        <div className="border-t border-ipc-border pt-4" data-testid="qlik-tier-status">
          {data.hasQlikCloud !== false && <QlikUpsellCard marketplaceListingUrl={marketplaceListingUrl} />}
          {canManage && (
            <Link className="mt-2 inline-block text-sm font-semibold text-ipc-light hover:underline" to={`${customerSectionPath(data.customerId, 'settings')}/company`}>
              Change tier
            </Link>
          )}
        </div>
      )}

      {!tierExcludesQlik && !isDeployed && !isEnabled && (
        <p className="text-sm text-ipc-muted">Available once the collector is deployed in the AWS account.</p>
      )}

      {!tierExcludesQlik && isDeployed && !isEnabled && !isPending && !canManage && (
        <p className="text-sm text-ipc-ink">The Qlik Cloud integration is not enabled yet. Ask IPC Global to enable it.</p>
      )}

      {!tierExcludesQlik && canManage && isDeployed && !isEnabled && !isPending && !confirming && (
        <div className="border-t border-ipc-border pt-4">
          <p className="text-sm text-ipc-ink">
            Enabling creates an IAM user with read-only access to the audit S3 bucket. Credentials are stored in AWS Systems Manager Parameter Store.
          </p>
          <button
            type="button"
            className="btn-primary mt-3 text-sm"
            onClick={() => setConfirming(true)}
            disabled={showBusy}
          >
            Enable Qlik integration
          </button>
        </div>
      )}

      {canManage && isDeployed && !isEnabled && !isPending && confirming && (
        <Notice
          tone="info"
          titleAs="p"
          title="Start the stack update?"
          action={(
            <div className="flex gap-3">
              <button type="button" className="btn-primary text-sm" onClick={() => toggle('enable')} disabled={showBusy}>
                {showBusy ? 'Updating stack...' : 'Confirm enable'}
              </button>
              <button type="button" className="btn-secondary text-sm" onClick={() => setConfirming(false)} disabled={showBusy}>
                Cancel
              </button>
            </div>
          )}
        >
          Enabling triggers a CloudFormation stack update in the customer account.
        </Notice>
      )}

      {isEnabled && (
        <QlikConnectionDetails
          data={data}
          canManage={canManage}
          confirming={confirming}
          setConfirming={setConfirming}
          busy={busy}
          onToggle={toggle}
        />
      )}

      {result && (
        <Notice tone={resultTone} role={resultTone === 'bad' ? 'alert' : 'status'} titleAs="p" title={result.text} />
      )}
    </SectionCard>
  );
}

// A small inline control: a 40 px touch target on touch layouts, compact from the sm breakpoint up.
const SMALL_BUTTON = 'inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center rounded border border-ipc-border bg-white px-2 text-xs font-medium text-ipc-ink hover:bg-ipc-surface sm:min-h-0 sm:min-w-0 sm:py-0.5';

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button type="button" onClick={copy} className={SMALL_BUTTON}>
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// The Amazon S3 V2 connection fields, in the order and with the labels of
// the Qlik Cloud dialog (help.qlik.com, "Amazon S3 V2", checked 2026-09-29).
function ConnectorField({ label, children, hint }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[11rem_minmax(0,1fr)] gap-x-4 gap-y-1 py-2 border-b border-ipc-border last:border-b-0">
      <dt className="text-xs font-semibold text-ipc-muted">{label}</dt>
      <dd className="min-w-0 text-xs text-ipc-ink">
        {children}
        {hint && <span className="block text-xs text-ipc-muted mt-0.5">{hint}</span>}
      </dd>
    </div>
  );
}

function CopyValue({ value }) {
  if (!value) return <span className="font-mono">--</span>;
  return (
    <span className="flex items-start gap-2">
      <span className="font-mono break-all flex-1 min-w-0">{value}</span>
      <CopyButton value={value} />
    </span>
  );
}

function QlikSetupSteps({ data }) {
  const { customerId, qlik } = data;
  return (
    <SectionCard
      title="Set up the Qlik Cloud data connection"
      description="For the Qlik Cloud admin of this customer."
      testId="qlik-setup-steps"
      bodyClassName="space-y-4"
    >
      <p className="text-sm text-ipc-ink">
        Create the connection once, in the space that holds the onGuard apps.
        The apps read it by its name, <span className="font-mono">{QLIK_S3_CONNECTION_NAME}</span>: with any other name, or in another space, their reload fails.
      </p>

      <ol className="ml-4 list-decimal space-y-2 text-sm text-ipc-ink">
        <li>In Qlik Cloud, open the shared or managed space where the onGuard apps live. You need a role in it that can manage data sources (for example <strong>Owner</strong>, <strong>Can manage</strong> or <strong>Can edit</strong>).</li>
        <li>Open an app in that space (or create one there), then open the <strong>Data load editor</strong>.</li>
        <li>Click <strong>Data sources</strong>, then <strong>Create new connection</strong>.</li>
        <li>Under <strong>Space</strong>, select that same space.</li>
        <li>Choose <strong>Amazon S3 V2</strong>.</li>
        <li>
          Fill in the fields exactly as below:
          <dl className="mt-2 rounded-md border border-ipc-border px-3">
            <ConnectorField label="Access key"><CopyValue value={qlik.accessKeyId} /></ConnectorField>
            <ConnectorField label="Secret key" hint="Shown only after you reveal it. Paste it straight into Qlik Cloud; never save it in a file or send it in a message.">
              {data.revealAvailable
                ? <RevealSecretField key={customerId} customerId={customerId} />
                : <span className="text-ipc-muted">Available while the customer is live and the integration is enabled.</span>}
            </ConnectorField>
            <ConnectorField label="Region"><CopyValue value={qlik.region} /></ConnectorField>
            <ConnectorField label="Bucket name"><CopyValue value={qlik.bucketName} /></ConnectorField>
            <ConnectorField label="Root prefix (optional)">Leave empty.</ConnectorField>
            <ConnectorField label="SSE header value (optional)" hint="The bucket encrypts with AES256. The value only matters when an app writes with STORE; these credentials are read-only."><CopyValue value="AES256" /></ConnectorField>
            <ConnectorField label="Name" hint="Exactly this, including the capital letters and the underscore."><CopyValue value={QLIK_S3_CONNECTION_NAME} /></ConnectorField>
          </dl>
        </li>
        <li>Click <strong>Test connection</strong>. When it succeeds, click <strong>Create</strong>.</li>
        <li>
          First load check: paste this line into the Data load editor of an app in that space, replace <span className="font-mono">{QLIK_SPACE_PLACEHOLDER}</span> with the space&apos;s name, and click <strong>Load data</strong>. A <span className="font-mono">Users</span> table means the connection works.
          <div className="mt-2 flex items-start gap-2 rounded-md border border-ipc-border p-2">
            <pre className="flex-1 min-w-0 whitespace-pre-wrap break-all font-mono text-xs text-ipc-ink">{QLIK_FIRST_LOAD_SAMPLE}</pre>
            <CopyButton value={QLIK_FIRST_LOAD_SAMPLE} />
          </div>
        </li>
      </ol>
      <p className="text-xs text-ipc-muted">
        The credentials are read-only and work only from Qlik Cloud. More load samples for every source are in the Data reference.
      </p>
    </SectionCard>
  );
}

// The Qlik secret is never part of a read (ENTRAID-279). Revealing it is an
// explicit, audited, rate limited, uncached call; the value lives only in
// this component's state, and is gone on Hide, when the section unmounts,
// or when the page switches to another customer (the caller keys this field
// by customer, and the effect below clears it too, so a value can never be
// shown under the wrong customer).
function RevealSecretField({ customerId }) {
  const [value, setValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A reveal still in flight when the customer changes must not land.
  const latestCustomer = useRef(customerId);
  useEffect(() => {
    latestCustomer.current = customerId;
    setValue(null);
    setError(null);
  }, [customerId]);

  async function reveal() {
    const forCustomer = customerId;
    setBusy(true);
    setError(null);
    try {
      const r = await api.revealQlikSecret(forCustomer);
      if (forCustomer !== latestCustomer.current) return;
      setValue(typeof r?.secretAccessKey === 'string' ? r.secretAccessKey : null);
      if (typeof r?.secretAccessKey !== 'string') setError('The credentials could not be revealed.');
    } catch (e) {
      if (forCustomer !== latestCustomer.current) return;
      setError(e?.status === 429 ? 'Revealed too many times in the last hour. Try again later.' : 'The credentials could not be revealed.');
    } finally {
      setBusy(false);
    }
  }

  if (!value) {
    return (
      <span className="block">
        <button
          type="button"
          onClick={reveal}
          disabled={busy}
          className={`${SMALL_BUTTON} disabled:opacity-50 sm:py-1`}
        >
          {busy ? 'Revealing...' : 'Reveal credentials'}
        </button>
        <span className="block text-xs text-ipc-muted mt-1">Each reveal is logged.</span>
        {error && <span role="alert" className="block text-xs text-red-700 mt-1">{error}</span>}
      </span>
    );
  }
  return (
    <span className="flex items-start gap-2">
      <span className="font-mono break-all flex-1 min-w-0">{value}</span>
      <CopyButton value={value} />
      <button type="button" onClick={() => setValue(null)} className={SMALL_BUTTON}>
        Hide
      </button>
    </span>
  );
}

function QlikConnectionDetails({ data, canManage, confirming, setConfirming, busy, onToggle }) {
  const { qlik } = data;
  return (
    <div className="space-y-3 border-t border-ipc-border pt-4">
      {canManage && qlik.iamUserName && (
        <p className="text-xs text-ipc-muted">IAM user in the customer account: <span className="font-mono">{qlik.iamUserName}</span></p>
      )}

      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-ipc-muted">
          Enabled {qlik.enabledAt ? new Date(qlik.enabledAt).toLocaleDateString() : ''}
          {canManage && qlik.ssmParameterName && (
            <span className="ml-2" title="Where the credentials are stored in the customer account">
              SSM: {qlik.ssmParameterName}
            </span>
          )}
        </p>
        {canManage && (!confirming ? (
          <button
            className="text-xs px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Disable
          </button>
        ) : (
          <div className="flex gap-2 items-center">
            <span className="text-xs text-red-600">Invalidates the Qlik data connection.</span>
            <button
              className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              onClick={() => onToggle('disable')}
              disabled={busy}
            >
              {busy ? 'Removing...' : 'Confirm'}
            </button>
            <button
              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-ipc-muted hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
